import { useCallback, useRef, useState, type ReactNode } from 'react';
import type { TaskSummary } from '../../lib/tasks';
import { formatDateOnly, isOverdue, shiftDateOnly, todayInputValue } from '../../lib/taskDates';
import { useDismiss } from '../../lib/useDismiss';
import { Button } from '../ui';
import { DateInput } from '../DateInput';
import { CalendarIcon } from '../icons';

/**
 * Setting a due date without opening the task.
 *
 * A popover rather than the click-to-edit swap the panel's title uses. The due
 * column is 3.5rem and `<input type="date">` has a user-agent minimum width it
 * will not go below -- roughly 7rem, browser depending -- so swapping one in
 * would force that group's grid open while it was on screen. And because every
 * status group is its own `<ul>`, only that group would widen: the columns stop
 * lining up mid-edit, which is the exact failure taskGrid.ts exists to prevent.
 *
 * The presets are the other half of the reason. Today and next week are most of
 * what anyone picks, and there is no keyboard-free way to clear a native date
 * input at all -- "no due date" has to be a row you can click.
 */

/** Shared by the row cell and the bulk bar, so both offer the same choices. */
export function DueDateMenu({
  value,
  min,
  allowClear = value !== null,
  onPick,
  onClose,
}: {
  /** The current date, or null for none. The bulk bar passes null: a selection
   *  has many dates and none of them is *the* value being edited. */
  value: string | null;
  /** Earliest date offered, so the server's "start date cannot be after the due
   *  date" rejection is prevented rather than reported. */
  min?: string | undefined;
  /** Defaults to "only when there is a date to clear", which is right for a
   *  single row. The bulk bar has to say so explicitly: it shows no value, and
   *  clearing the dates on a batch is a thing you would want to do. */
  allowClear?: boolean;
  onPick: (value: string | null) => void;
  /** Closes without picking: Set pressed on an untouched field. */
  onClose: () => void;
}) {
  const today = todayInputValue();
  const fieldRef = useRef<HTMLInputElement>(null);

  // One pick per opening. A preset closes the panel, and the date field, as it
  // unmounts, commits whatever draft it was holding -- which must not land on
  // top of the preset that was actually clicked.
  const picked = useRef(false);
  function pick(next: string | null) {
    if (picked.current) return;
    picked.current = true;
    onPick(next);
  }

  return (
    <>
      <MenuRow onClick={() => pick(today)}>Today</MenuRow>
      <MenuRow onClick={() => pick(shiftDateOnly(today, 1))}>Tomorrow</MenuRow>
      <MenuRow onClick={() => pick(shiftDateOnly(today, 7))}>Next week</MenuRow>

      <div className="mt-1 flex items-center gap-1.5 border-t border-stone-100 px-1 pb-1 pt-2">
        {/* Wrapped rather than given a width here: the primitive is `w-full`,
            and Tailwind's emit order means a caller's width class does not
            reliably beat it. */}
        <div className="min-w-0 flex-1">
          {/* Commits on Enter, on blur, or when the panel closes -- not on
              change, which the calendar fires just for paging to another
              month (see DateInput). */}
          <DateInput
            ref={fieldRef}
            compact
            aria-label="Due date"
            value={value ?? ''}
            min={min}
            onCommit={(next) => {
              // Clearing the field with the keyboard reads as "no due date",
              // and is the only way to say it without the row below.
              pick(next === '' ? null : next);
            }}
          />
        </div>
        {/* The obvious way to finish, now that choosing a day in the calendar
            no longer does. Reads the field itself rather than waiting on its
            blur, which a click here does not cause (see MenuRow). */}
        <Button
          compact
          variant="primary"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            const field = fieldRef.current;
            if (!field || field.validity.badInput) return;
            const next = field.value === '' ? null : field.value;
            // Nothing changed: just close. Re-sending the same date would be a
            // wasted save -- and in the bulk bar, where `value` is null, an
            // untouched field would clear every selected task's date.
            if (next === value) onClose();
            else pick(next);
          }}
        >
          Set
        </Button>
      </div>

      {allowClear ? (
        <div className="border-t border-stone-100 pt-1">
          <MenuRow onClick={() => pick(null)}>Clear due date</MenuRow>
        </div>
      ) : null}
    </>
  );
}

function MenuRow({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      // Keeps focus in the date field, so its blur commit does not close the
      // panel out from under this click (`pick` then ignores the draft it
      // commits on unmount instead).
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className="flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm text-stone-700 transition hover:bg-stone-50"
    >
      {children}
    </button>
  );
}

/**
 * The panel shell. Anchored `right-0`: the page's scroll container computes
 * `overflow-x: auto`, so a panel growing rightwards out of a narrow column adds
 * a horizontal scrollbar to the whole page.
 */
function Panel({ children }: { children: ReactNode }) {
  return (
    <div
      role="dialog"
      aria-label="Due date"
      className="absolute right-0 top-full z-20 mt-1.5 w-56 rounded-xl border border-stone-200 bg-white p-1 shadow-lg"
    >
      {children}
    </div>
  );
}

/** The list row's due cell. */
export function DueDateCell({
  task,
  taskTitle = task.title,
  showOnPhone = false,
  disabled,
  onPick,
}: {
  task: TaskSummary;
  /** Named in the accessible label, the same as StatusCell's. Defaults to the
   *  bare title, which is right inside a project; the dashboard passes the
   *  parent-qualified name, because "change the due date of on Mac" is not a
   *  sentence anyone can act on. */
  taskTitle?: string;
  /**
   * Whether the cell survives below `sm:`. It does not by default: TASK_GRID
   * drops the due track on a phone, and a cell rendered into a column that is
   * not there would sit on top of the priority dot. DAY_GRID keeps that track
   * at every width -- it is the column that card exists for -- so the dashboard
   * asks for it back.
   */
  showOnPhone?: boolean;
  disabled: boolean;
  onPick: (value: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  useDismiss(containerRef, open, useCallback(() => setOpen(false), []));

  const overdue = isOverdue(task.dueDate) && task.status !== 'DONE';

  return (
    <span
      ref={containerRef}
      // z-10 is what keeps the trigger out from under the row-filling `<Link>`.
      // Every other row's due cell carries it too, though, and at equal z-index
      // the later one in the document wins -- so an open panel was being drawn
      // through by the cells of the rows below it. Lifting it while open clears
      // them, and stays under the bulk bar, which is what should cover it when
      // the list scrolls.
      //
      // Both visibilities spelled out in full rather than composed from a
      // prefix, because Tailwind scans source text and would never generate a
      // class name assembled at runtime.
      className={`relative ${showOnPhone ? 'block' : 'hidden sm:block'} ${open ? 'z-20' : 'z-10'}`}
    >
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          task.dueDate
            ? `Due ${formatDateOnly(task.dueDate)}. Change the due date of ${taskTitle}`
            : `Set a due date for ${taskTitle}`
        }
        onClick={() => setOpen((value) => !value)}
        // The negative margin eats half the grid gutter on each side, so the
        // target is wider than the 3.5rem column without the text moving.
        className={`-mx-1 block w-[calc(100%+0.5rem)] truncate rounded px-1 py-0.5 text-left text-meta transition hover:bg-stone-200/60 disabled:cursor-default disabled:hover:bg-transparent ${
          overdue
            ? 'font-medium text-red-600'
            : task.dueDate
              ? 'text-stone-500'
              : // An undated cell used to render nothing at all, so there was
                // no target to click. Transparent text still hit-tests, which
                // is what is wanted here -- and specifically what is *not*
                // wanted for the row's hover buttons, which use `invisible`.
                'text-transparent group-hover:text-stone-500'
        }`}
      >
        {task.dueDate ? formatDateOnly(task.dueDate) : 'Due'}
      </button>

      {open ? (
        <Panel>
          <DueDateMenu
            value={task.dueDate}
            min={task.startDate ?? undefined}
            onPick={(value) => {
              setOpen(false);
              onPick(value);
            }}
            onClose={() => setOpen(false)}
          />
        </Panel>
      ) : null}
    </span>
  );
}

/** The bulk bar's trigger. Same menu, no current value to show. */
export function DueDateButton({ onPick }: { onPick: (value: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useDismiss(containerRef, open, useCallback(() => setOpen(false), []));

  return (
    <div ref={containerRef} className="relative">
      <Button compact aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <CalendarIcon className="h-3.5 w-3.5" />
        Due date
      </Button>

      {open ? (
        <Panel>
          {/* No `min`: a selection has many start dates and no single floor.
              Rows the server rejects revert on their own and say why. */}
          <DueDateMenu
            value={null}
            allowClear
            onPick={(value) => {
              setOpen(false);
              onPick(value);
            }}
            onClose={() => setOpen(false)}
          />
        </Panel>
      ) : null}
    </div>
  );
}
